import React from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { Avatar } from "antd";
import { IMAGE_BASE_URL } from "../../config/api";
import CommentBadge from "./CommentBadge";
import { formatTimeAndDate } from "../../utils/dateUtils";

const ProgressCard = ({ progress, onProgressClick, workerNameById = {} }) => {
  // Use actual creation timestamp first (matches mobile behavior).
  // `date` is often date-only and can show incorrect clock time after timezone conversion.
  const progressTimestamp = progress?.createdAt || progress?.created_at || progress?.date;
  const isValidWorkerName = (value) => {
    if (typeof value !== "string") return false;
    const trimmed = value.trim();
    if (!trimmed) return false;
    // Prevent showing ids like "12" as worker name.
    if (/^\d+$/.test(trimmed)) return false;
    return true;
  };

  const workerNameCandidates = [
    progress?.User?.username,
    progress?.Worker?.username,
    progress?.creator?.username,
    progress?.worker_name,
    progress?.workerName,
    progress?.username,
    progress?.created_by_name,
    progress?.createdByName,
  ];

  const fallbackNameFromId =
    workerNameById?.[String(progress?.created_by)] ||
    workerNameById?.[String(progress?.user_id)] ||
    workerNameById?.[String(progress?.worker_id)] ||
    null;

  const workerName =
    workerNameCandidates.find((name) => isValidWorkerName(name)) ||
    (isValidWorkerName(fallbackNameFromId) ? fallbackNameFromId : "Unknown Worker");

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
  };

  const renderAttachments = (attachments) => {
    return attachments.map((attachment, index) => {
      const fileUrl = `${IMAGE_BASE_URL}${attachment.url}`;
      
      if (attachment.type && attachment.type.startsWith('image/')) {
        return (
          <div key={attachment.id}>
            <img
              src={fileUrl}
              alt={`Attachment ${index + 1}`}
              className="w-full h-[320px] rounded-[4px] object-cover"
            />
          </div>
        );
      } else if (attachment.type && attachment.type.startsWith('video/')) {
        return (
          <div key={attachment.id}>
            <video
              controls
              className="w-full max-h-[360px] rounded-[4px] object-contain bg-black"
            >
              <source src={fileUrl} type={attachment.type} />
              Your browser does not support the video tag.
            </video>
          </div>
        );
      } else if (attachment.type && attachment.type.startsWith('audio/')) {
        return (
          <div key={attachment.id} className="w-full rounded-[4px] bg-gray-100 flex items-center justify-center p-4">
            <div className="w-full max-w-md">
              <p className="text-center mb-4 text-gray-600">{attachment.original_name}</p>
              <audio controls className="w-full">
                <source src={fileUrl} type={attachment.type} />
                Your browser does not support the audio element.
              </audio>
            </div>
          </div>
        );
      }
      return (
        <div key={attachment.id} className="p-4 border rounded-md mb-2">
          <a 
            href={fileUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            {attachment.original_name}
          </a>
        </div>
      );
    });
  };

  return (
    <div 
      className="bg-white flex flex-col gap-4 rounded-[8px] py-4 px-4 w-full cursor-pointer"
      onClick={() => onProgressClick?.(progress.id)}
    >
      <div   className="flex items-center justify-between ">
        <div className="flex items-center gap-3">
          <Avatar 
            src={progress.User?.image ? `${IMAGE_BASE_URL}${progress.User.image}` : null}
            className="w-10 h-10 rounded-[4px]"
          >
            {workerName?.[0]?.toUpperCase() || 'U'}
          </Avatar>
          <div className="flex flex-col">
            <span className="font-medium text-[15px] text-[#4B5563]">
              {workerName}
            </span>
            <span className="text-[13px] font-normal text-[#B7B7B7]">
              <span title={formatTimeAndDate(progressTimestamp)}>
                {formatTimeAndDate(progressTimestamp)}
              </span>
            </span>
          </div>
        </div>
        <div className="text-right">
          <div className="flex flex-col">
            <span className="text-[14px] font-medium text-[#4B5563]">
              {progress.Project?.title}
            </span>
            <span className="text-[12px] text-[#B7B7B7]">
              Task: {progress.TaskProgress?.title}
            </span>
          </div>
        </div>
      </div>

      {progress.title && (
        <div className="border-l-4 border-[#F97316] pl-3 mt-1">
          <h3 className="text-[16px] font-medium text-[#4B5563] flex items-center gap-2">
            {progress.title}
            <span className="text-[13px] text-[#9CA3AF] font-normal">
              - {workerName}
            </span>
          </h3>
        </div>
      )}

      {progress.description && (
        <p className="text-[14px] text-[#4B5563] font-normal mt-1">
          {progress.description}
        </p>
      )}

      {progress.ProgressAttachments?.length > 0 && (
        <div onClick={() => onProgressClick?.(progress.id)} className="relative ">
        <div className="mt-2">
          <Slider {...settings}>
            {renderAttachments(progress.ProgressAttachments)}
          </Slider>
        </div>
        {progress.ProgressComments?.length > 0 && (
                <CommentBadge progress={true} count={progress.ProgressComments?.length} />
        )}
      </div>
      )}
    </div>
  );
};

export default ProgressCard; 
